'use client'

import { ReactNode, useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'

import { FormPreview } from '@/entities/contract'
import { isNilOrEmptyString, useEnhancedFormContext } from '@/shared/lib'
import { Spinner } from '@/shared/ui'

import { AccountForm } from './account-form'
import { useAccountVerification } from '../lib/account-verification'

type Props = {
  defaultFormOpen: boolean
  hasDefaultAccount: boolean
  description?: string
  onFormOpen?: () => void
  isEditable?: boolean
  LoadingComponent?: ReactNode
}

export const AccountUpdate = ({
  defaultFormOpen = false,
  hasDefaultAccount = false,
  description,
  onFormOpen,
  isEditable = true,
  LoadingComponent,
}: Props) => {
  const [isFormOpen, setIsFormOpen] = useState(defaultFormOpen)

  const { setValue, control } = useEnhancedFormContext()

  const [bankName, bankAccount, accountHolder] = useWatch({
    control,
    name: ['bankName', 'bankAccount', 'accountHolder'],
  })

  const { isAccountVerified, markAsUnverified, action } =
    useAccountVerification()

  const getDisplayAccount = (): string | null => {
    if (isNilOrEmptyString(bankName) || isNilOrEmptyString(bankAccount)) {
      return null
    }

    return `${bankName} ${bankAccount} (${accountHolder})`
  }

  const handleEditButtonClick = () => {
    setIsFormOpen(true)
    setValue('accountHolder', '')
    onFormOpen?.()

    if (isAccountVerified) {
      markAsUnverified()
    }
  }

  useEffect(() => {
    if (!hasDefaultAccount) {
      setIsFormOpen(defaultFormOpen)

      if (defaultFormOpen) {
        onFormOpen?.()
      }
    }
  }, [hasDefaultAccount, defaultFormOpen])

  if (!isFormOpen) {
    return (
      <FormPreview
        name="계좌 정보"
        value={getDisplayAccount()}
        onEditButtonClick={handleEditButtonClick}
        isEditable={isEditable}
      />
    )
  }

  return (
    <>
      {description && (
        <p className="typo-label2 -mt-2 mb-3 font-normal text-gray-500">
          {description}
        </p>
      )}
      <AccountForm onEditButtonClick={handleEditButtonClick} />
      {action.isExecuting &&
        (LoadingComponent ?? <Spinner className="rounded-xl bg-black/20" />)}
    </>
  )
}
